/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 *
 * This  software  has  been  provided pursuant  to  a  License
 * Agreement  containing  restrictions on  its  use.   The  software
 * contains  valuable  trade secrets and proprietary information  of
 * Pegasystems Inc and is protected by  federal   copyright law.  It
 * may  not be copied,  modified,  translated or distributed in  any
 * form or medium,  disclosed to third parties or used in any manner
 * not provided for in  said  License Agreement except with  written
 * authorization from Pegasystems Inc.
 */

const DEFAULT_BUFFER_WAIT_TIME = 100;
const MAX_BUFFER_SIZE = 500;

class BufferedDataQueue<T> {
  // grouped on parameter and Filter columns
  private readonly groupedDataViewArray: (T & {
    dataViewCallMap: {
      [identifier: string]: { promise: Promise<unknown>; resolve: (value: unknown) => void };
    };
  })[];

  private timeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly finalDataViewCall: (arg: T) => Promise<unknown>,
    private readonly combiner: (dataViewCallA: T, dataViewCallB: T) => void,
    private readonly comparator: (dataViewCallA: T, dataViewCallB: T) => boolean,
    private readonly separator: (
      combinedDataViewCall: T & {
        dataViewCallMap: {
          [identifier: string]: { promise: Promise<unknown>; resolve: (value: unknown) => void };
        };
      },
      data: unknown
    ) => void,
    private readonly maxtime: number = DEFAULT_BUFFER_WAIT_TIME,
    private readonly maxsize: number = MAX_BUFFER_SIZE
  ) {
    this.groupedDataViewArray = [];
  }

  findCombinableDataViewCall(newDataViewCall: T) {
    return this.groupedDataViewArray.find((combinedDataViewCall) =>
      this.comparator(combinedDataViewCall, newDataViewCall)
    );
  }

  dequeueAllDataViewCalls() {
    this.groupedDataViewArray.reverse();
    while (this.groupedDataViewArray.length > 0) {
      const combinedDataViewCall = this.groupedDataViewArray.pop() as (typeof this.groupedDataViewArray)[number];
      this.finalDataViewCall(combinedDataViewCall).then((data) => {
        this.separator(combinedDataViewCall, data);
      });
    }
    clearTimeout(this.timeoutId);
  }

  dequeueDataViewCallOnSizeExceed(dataViewCall: (typeof this.groupedDataViewArray)[number]) {
    const promiseIndex = this.groupedDataViewArray.findIndex((combinedDataViewCall) =>
      this.comparator(combinedDataViewCall, dataViewCall)
    );
    if (this.groupedDataViewArray[promiseIndex]) {
      this.groupedDataViewArray.splice(promiseIndex, 1);
      this.finalDataViewCall(dataViewCall).then((data) => {
        this.separator(dataViewCall, data);
      });
    }
  }

  queueDataViewCall(newDataViewCall: T, identifier: string) {
    let combinableDataViewCall = this.findCombinableDataViewCall(newDataViewCall);
    let promise: Promise<unknown>;
    let resolve: unknown;

    promise = new Promise((res) => {
      resolve = res;
    });
    if (combinableDataViewCall && !combinableDataViewCall.dataViewCallMap[identifier]) {
      // if we have similar Promise then combine
      this.combiner(combinableDataViewCall, newDataViewCall);
      combinableDataViewCall.dataViewCallMap[identifier] = { promise, resolve: resolve as (value: unknown) => void };
    } else if (combinableDataViewCall) {
      // if we have the same Promise then return the earlier promise
      promise = combinableDataViewCall.dataViewCallMap[identifier].promise;
    } else {
      // if we don't have similar promise create a new one
      combinableDataViewCall = {
        ...newDataViewCall,
        dataViewCallMap: { [identifier]: { promise, resolve: resolve as (value: unknown) => void } }
      };
      this.groupedDataViewArray.push({
        ...newDataViewCall,
        dataViewCallMap: { [identifier]: { promise, resolve: resolve as (value: unknown) => void } }
      });
    }
    if (Object.keys(combinableDataViewCall.dataViewCallMap).length === this.maxsize) {
      this.dequeueDataViewCallOnSizeExceed(combinableDataViewCall);
    } else {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => this.dequeueAllDataViewCalls(), this.maxtime);
    }

    return promise;
  }
}

export default BufferedDataQueue;
